import { useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getAvailableOpportunities } from '@swf/core/models/sports/betSlip/utils/getAvailableOpportunities';
import { select } from '@swf/core/store';

import type { FormData } from '../types';
import { calculateStake } from '../utils/calculateStake';
import { calculateTotalPotentialReturns } from '../utils/calculateTotalPotentialReturns';

const useTotals = () => {
  const { values } = useFormState<FormData>();
  const opportunities = useSelector(select.betSlip.opportunities);
  const wallets = useSelector(select.betSlip.wallets);

  const allBets = getAvailableOpportunities([...opportunities.SINGLE, ...opportunities.MULTIPLE]);

  const totalStake = allBets.reduce((acc, { id, numberOfLines = 0 }) => {
    const { stakePerLineParsed: stakePerLine = 0, eachWaySelected } = values[id] || {};
    return acc + calculateStake(stakePerLine, numberOfLines, eachWaySelected);
  }, 0);

  const bonusCashStake = allBets.reduce((acc, { id }) => {
    const [bonusCashWalletId] = values[id]?.bonusCash ?? [];
    const extraWalletApplied = bonusCashWalletId ? wallets[bonusCashWalletId] : undefined;
    if (!bonusCashWalletId || !extraWalletApplied) return acc;
    return acc + extraWalletApplied.amount;
  }, 0);

  const freeBetsStake = allBets.reduce((acc, { id }) => {
    const [freeBetsWalletId] = values[id]?.freeBet ?? [];
    const extraWalletApplied = freeBetsWalletId ? wallets[freeBetsWalletId] : undefined;
    if (!freeBetsWalletId || !extraWalletApplied) return acc;
    return acc + extraWalletApplied.amount;
  }, 0);

  const totalPotentialReturns = allBets.reduce(
    (acc, { id, numberOfLines = 0, returnFactor = 0, placeReturnFactor = 0 }) => {
      const { stakePerLineParsed: stakePerLine = 0, eachWaySelected, freeBet } = values[id] || {};

      return (
        acc +
        calculateTotalPotentialReturns({
          stakePerLine,
          numberOfLines,
          returnFactor,
          placeReturnFactor,
          eachWaySelected,
          isFreeBet: !!freeBet?.length
        })
      );
    },
    0
  );

  return {
    totalStake: isNaN(totalStake) ? 0 : totalStake,
    totalPotentialReturns,
    bonusCashStake,
    freeBetsStake
  };
};

export default useTotals;
